/**
 * Utilities for loading and using product matching reference tables.
 * Matching groups are persisted on the API and synced when datasets load.
 */

import {
  STICK_CATEGORY_HNS_GROUP_NAMES,
  STICK_CATEGORY_HNS_LINES,
  STICK_CATEGORY_HTS_GROUP_NAMES,
  STICK_CATEGORY_HTS_LINES,
  STICK_CATEGORY_VARIANT_GROUP_NAMES,
} from './constants';

export const MATCHING_STORAGE_KEY_PREFIX = 'product-matching-ref';

/** Selection key prefix for model-series nodes in Price & Promotions product filter. */
export const MODEL_GROUP_SELECTION_PREFIX = '__mg__:';

/** Selection key prefix for matching-group model nodes (must not collide with SKU titles). */
export const MODEL_SELECTION_PREFIX = '__model__:';

export function isModelGroupSelection(sel) {
  return typeof sel === 'string' && sel.startsWith(MODEL_GROUP_SELECTION_PREFIX);
}

export function modelGroupSelectionValue(name) {
  return `${MODEL_GROUP_SELECTION_PREFIX}${String(name || '').trim()}`;
}

export function parseModelGroupSelection(sel) {
  if (!isModelGroupSelection(sel)) return null;
  return sel.slice(MODEL_GROUP_SELECTION_PREFIX.length);
}

export function isModelSelection(sel) {
  return typeof sel === 'string' && sel.startsWith(MODEL_SELECTION_PREFIX);
}

export function modelSelectionValue(name) {
  return `${MODEL_SELECTION_PREFIX}${String(name || '').trim()}`;
}

export function parseModelSelection(sel) {
  if (!isModelSelection(sel)) return null;
  return sel.slice(MODEL_SELECTION_PREFIX.length);
}

/**
 * Expand filter selections (model series / matching-group model / SKU) to concrete SKU titles.
 * @param {string[]} selected
 * @param {Map<string, string[]>} groupNameToProducts - matching group name -> SKU titles
 * @param {Map<string, string[]>} modelGroupToProducts - model series name -> SKU titles
 * @returns {Set<string>}
 */
export function expandProductSelections(selected, groupNameToProducts, modelGroupToProducts) {
  const expanded = new Set();
  (selected || []).forEach((sel) => {
    if (!sel) return;
    const modelGroupName = parseModelGroupSelection(sel);
    if (modelGroupName != null) {
      const list = modelGroupToProducts?.get?.(modelGroupName);
      if (list?.length) list.forEach((t) => expanded.add(t));
      return;
    }
    const modelName = parseModelSelection(sel);
    if (modelName != null) {
      const list = groupNameToProducts?.get?.(modelName);
      if (list?.length) list.forEach((t) => expanded.add(t));
      else expanded.add(modelName);
      return;
    }
    // Legacy unprefixed model name: expand only when that name is not itself a SKU
    // in the group. A namesake SKU (e.g. model "Ploom AURA" + SKU "Ploom AURA")
    // must stay a single-title selection.
    const groupList = groupNameToProducts?.get?.(sel);
    if (groupList?.length && !groupList.includes(sel)) {
      groupList.forEach((t) => expanded.add(t));
      return;
    }
    expanded.add(sel);
  });
  return expanded;
}

/**
 * Convert legacy unprefixed matching-group names to `__model__:` keys.
 * A namesake SKU (same title as its model) is upgraded only when no other
 * member of that group is also selected — that was always "whole model" before
 * model keys were namespaced.
 */
export function upgradeLegacyModelSelections(selected, groupNameToProducts) {
  if (!selected?.length) return selected || [];
  const selectedSet = new Set(selected);
  return selected.map((sel) => {
    if (!sel || isModelGroupSelection(sel) || isModelSelection(sel)) return sel;
    const members = groupNameToProducts?.get?.(sel);
    if (!members?.length) return sel;
    if (members.includes(sel)) {
      const otherMemberSelected = members.some((sku) => sku !== sel && selectedSet.has(sku));
      if (otherMemberSelected) return sel;
    }
    return modelSelectionValue(sel);
  });
}

/** Human-readable label for a product-filter selection value. */
export function productSelectionLabel(sel) {
  const mg = parseModelGroupSelection(sel);
  if (mg != null) return mg;
  const model = parseModelSelection(sel);
  return model != null ? model : sel;
}

/** Sanitize variant string: trim, collapse spaces, remove symbols like ™ ® © */
export function sanitizeVariant(str) {
  if (str == null || typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[™®©§¶†‡•]+/g, '')
    .trim();
}

/**
 * Convert a Python dict/list repr to JSON. Preserves already-double-quoted strings
 * (needed for Italian copy with apostrophes, e.g. "l'acquisto").
 */
function pythonLiteralToJson(str) {
  const s = String(str)
    .replace(/\bNone\b/g, 'null')
    .replace(/\bTrue\b/g, 'true')
    .replace(/\bFalse\b/g, 'false');
  let out = '';
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (c !== '"' && c !== "'") {
      out += c;
      continue;
    }
    const quote = c;
    let inner = '';
    i += 1;
    while (i < s.length) {
      const ch = s[i];
      if (ch === '\\' && i + 1 < s.length) {
        inner += ch + s[i + 1];
        i += 2;
        continue;
      }
      if (ch === quote) break;
      inner += ch;
      i += 1;
    }
    const jsonInner = inner.replace(/\\'/g, "'").replace(/"/g, '\\"');
    out += `"${jsonInner}"`;
  }
  return out;
}

function isStructuredVariantBlob(str) {
  const s = String(str || '').trim();
  if (!s) return false;
  if (s.startsWith('[') || s.startsWith('{')) return true;
  return /['"]\w+['"]\s*:/.test(s);
}

function isGarbageVariantLabel(text) {
  const t = String(text || '').trim();
  if (!t) return true;
  if (/[{}\[\]]/.test(t)) return true;
  if (/^\s*['"]?\w+['"]?\s*:/.test(t)) return true;
  if (t.includes("':") || t.includes('":')) return true;
  return false;
}

const VARIANT_NAME_KEYS = [
  'product_variant_name',
  'product_label',
  'product_variant_color',
  'name',
  'label',
];

/** Pull product `name` values from objects that also have a sku (Magento flavour lists). */
function extractNamesFromProductRepr(str) {
  const names = [];
  String(str || '')
    .split('{')
    .slice(1)
    .forEach((chunk) => {
      if (!/['"]sku['"]\s*:/.test(chunk)) return;
      const match = chunk.match(/['"]name['"]\s*:\s*['"]([^'"]+)['"]/);
      if (match?.[1]) names.push(match[1]);
    });
  return names;
}

/** Extract variant strings from raw value: handles list of dicts (product_variant_name), nested lists, plain strings, Python repr. */
export function extractVariantsFromRaw(raw) {
  const results = [];
  const add = (v) => {
    let t = sanitizeVariant(v);
    const wrapped = t.match(/^['"](.+)['"]$/);
    if (wrapped) t = sanitizeVariant(wrapped[1]);
    if (!t || ['n/a', 'none', 'null'].includes(t.toLowerCase())) return;
    if (isGarbageVariantLabel(t)) return;
    results.push(t);
  };
  const processEntry = (entry) => {
    if (entry == null) return;
    if (Array.isArray(entry)) {
      entry.forEach(processEntry);
      return;
    }
    if (typeof entry === 'object') {
      for (const key of VARIANT_NAME_KEYS) {
        const name = entry[key];
        if (typeof name === 'string' && name.trim()) {
          add(name);
          return;
        }
      }
      return;
    }
    if (typeof entry === 'string') add(entry);
  };

  if (raw == null) return results;
  if (Array.isArray(raw) || typeof raw === 'object') {
    processEntry(raw);
    return results;
  }

  const str = String(raw).trim();
  if (!str) return results;

  if (isStructuredVariantBlob(str)) {
    try {
      const parsed = JSON.parse(pythonLiteralToJson(str));
      processEntry(parsed);
      if (results.length) return results;
    } catch {
      /* fall through to sku/name regex */
    }
    extractNamesFromProductRepr(str).forEach(add);
    return results;
  }

  str.split(/\s*[,|;]\s*/).forEach((v) => add(v));
  return results;
}

const PACKAGE_VARIANT_NAME_KEYS = [
  'package_variant_name',
  'package_name',
  'packaging',
  'pack_name',
  'product_label',
  'label',
  'name',
  'title',
  'size',
  'pack_size',
  'units',
  'Units (#)',
  'unit',
];

function addPackageVariantLabel(results, value) {
  if (value == null || value === '') return;
  if (typeof value === 'number' && Number.isFinite(value)) {
    results.push(String(value));
    return;
  }
  const text = sanitizeVariant(String(value));
  if (text && !['n/a', 'none', 'null', 'nan'].includes(text.toLowerCase())) {
    results.push(text);
  }
}

function processPackageVariantEntry(entry, results) {
  if (entry == null) return;
  if (Array.isArray(entry)) {
    entry.forEach((item) => processPackageVariantEntry(item, results));
    return;
  }
  if (typeof entry === 'number' && Number.isFinite(entry)) {
    addPackageVariantLabel(results, entry);
    return;
  }
  if (typeof entry === 'object') {
    for (const key of PACKAGE_VARIANT_NAME_KEYS) {
      if (entry[key] != null && entry[key] !== '') {
        addPackageVariantLabel(results, entry[key]);
        return;
      }
    }
    for (const val of Object.values(entry)) {
      if (typeof val === 'string' || (typeof val === 'number' && Number.isFinite(val))) {
        addPackageVariantLabel(results, val);
        return;
      }
    }
    return;
  }
  if (typeof entry === 'string') addPackageVariantLabel(results, entry);
}

const PACKAGE_ACTUAL_PRICE_KEYS = ['actual_price', 'price', 'current_price', 'final_price'];
const PACKAGE_REGULAR_PRICE_KEYS = ['regular_price', 'original_price', 'was_price'];
const PACKAGE_UNITS_KEYS = ['units', 'pack_size', 'Units (#)', 'unit', 'qty', 'quantity'];

function firstDefinedField(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== '') return obj[key];
  }
  return null;
}

function labelFromPackageEntry(entry) {
  if (entry == null) return '';
  if (typeof entry === 'number' && Number.isFinite(entry)) return String(entry);
  if (typeof entry === 'string') return sanitizeVariant(entry);
  if (typeof entry !== 'object') return '';
  for (const key of PACKAGE_VARIANT_NAME_KEYS) {
    if (entry[key] != null && entry[key] !== '') {
      return typeof entry[key] === 'number' ? String(entry[key]) : sanitizeVariant(String(entry[key]));
    }
  }
  return '';
}

function processPackageVariantOffer(entry, results) {
  if (entry == null) return;
  if (Array.isArray(entry)) {
    entry.forEach((item) => processPackageVariantOffer(item, results));
    return;
  }
  if (typeof entry === 'number' && Number.isFinite(entry)) {
    results.push({
      label: String(entry),
      units: entry,
      actual_price: null,
      regular_price: null,
      currency: null,
    });
    return;
  }
  if (typeof entry === 'object') {
    const label = labelFromPackageEntry(entry);
    if (!label || ['n/a', 'none', 'null', 'nan'].includes(label.toLowerCase())) return;
    const unitsRaw = firstDefinedField(entry, PACKAGE_UNITS_KEYS);
    const unitsNum = unitsRaw == null || unitsRaw === '' ? NaN : Number(unitsRaw);
    results.push({
      label,
      units: Number.isFinite(unitsNum) && unitsNum > 0 ? unitsNum : null,
      actual_price: firstDefinedField(entry, PACKAGE_ACTUAL_PRICE_KEYS),
      regular_price: firstDefinedField(entry, PACKAGE_REGULAR_PRICE_KEYS),
      currency: entry.currency || null,
    });
    return;
  }
  if (typeof entry === 'string') {
    const label = sanitizeVariant(entry);
    if (label && !['n/a', 'none', 'null', 'nan'].includes(label.toLowerCase())) {
      results.push({
        label,
        units: null,
        actual_price: null,
        regular_price: null,
        currency: null,
      });
    }
  }
}

function parsePackageVariantsContainer(raw, processFn, results) {
  if (raw == null) return;
  if (Array.isArray(raw) || typeof raw === 'object') {
    processFn(raw, results);
    return;
  }
  const str = String(raw).trim();
  if (!str || ['n/a', 'none', 'null', 'nan', '[]'].includes(str.toLowerCase())) return;
  if (str.startsWith('[') || str.startsWith('{')) {
    try {
      const value = JSON.parse(pythonLiteralToJson(str));
      processFn(value, results);
      if (results.length) return;
    } catch {
      /* fall through */
    }
  }
  str.split(/\s*[,|;]\s*/).forEach((part) => processFn(part, results));
}

/** Extract packaging labels from package_variants: lists, dicts, Python/JSON strings, numbers. */
export function extractPackageVariantsFromRaw(raw) {
  const results = [];
  parsePackageVariantsContainer(raw, processPackageVariantEntry, results);
  return results;
}

/** Packaging offers with optional price/units from package_variants objects. */
export function extractPackageVariantOffersFromRaw(raw) {
  const results = [];
  parsePackageVariantsContainer(raw, processPackageVariantOffer, results);
  return results;
}

export function formatPackageVariantLabel(value) {
  const text = String(value ?? '').trim();
  if (/^\d+(\.\d+)?$/.test(text)) {
    const n = Number(text);
    if (Number.isFinite(n)) return Number.isInteger(n) ? `${n}-pack` : `${text}-pack`;
  }
  return text;
}

export function inferPackSizeFromLabel(label) {
  const text = String(label ?? '').trim();
  if (!text) return null;
  if (/^\d+(\.\d+)?$/.test(text)) {
    const n = Number(text);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const packMatch = text.match(/(\d+(?:\.\d+)?)\s*-?\s*pack\b/i);
  if (packMatch) {
    const n = Number(packMatch[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

/** Unique packaging labels for a SKU across its dataset rows. */
export function uniquePackageVariantsFromRows(rows) {
  const seen = new Map();
  (rows || []).forEach((row) => {
    const raw = row?.package_variants ?? row?.package_variants_data;
    extractPackageVariantsFromRaw(raw).forEach((label) => {
      const key = label.toLowerCase();
      if (!seen.has(key)) seen.set(key, label);
    });
  });
  return [...seen.values()].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
  });
}

/** Flat list of variants from a group (supports variantGroups or legacy variants). Sanitizes each variant. */
export function getFlatVariantsFromGroup(group) {
  const raw = group?.variantGroups?.length
    ? group.variantGroups.flatMap((vg) => vg.variants || [])
    : (Array.isArray(group?.variants) ? group.variants : []);
  return raw.map((v) => sanitizeVariant(v)).filter(Boolean);
}

export function getMatchingStorageKey(country, retailer) {
  return `${MATCHING_STORAGE_KEY_PREFIX}-${country || ''}-${retailer || ''}`;
}

function parseCountryRetailerFromStorageKey(storageKey) {
  if (!storageKey || !storageKey.startsWith(`${MATCHING_STORAGE_KEY_PREFIX}-`)) {
    return { country: '', retailer: '' };
  }
  const parts = storageKey.replace(`${MATCHING_STORAGE_KEY_PREFIX}-`, '').split('-');
  return {
    country: parts[0] || '',
    retailer: parts.slice(1).join('-') || '',
  };
}

function generateMatchingId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Signature for de-duplicating the same logical group row on one SKU. */
function matchingInfoSignature(info) {
  if (!info) return '';
  return `${info.groupName || ''}\0${(info.country || '').toLowerCase()}\0${(info.retailer || '').toLowerCase()}`;
}

/**
 * All matching-group rows for one SKU (map values may be a single object or an array after multi-group assign).
 */
export function matchingInfosForSku(map, sku) {
  if (!map || sku == null || sku === '') return [];
  const v = map.get(sku);
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
}

/** Merge one membership into the map (same SKU may appear in several matching groups). */
export function mergeMatchingSkuInfo(targetMap, sku, info) {
  if (!targetMap || !sku || !info) return;
  const cur = targetMap.get(sku);
  if (cur == null) {
    targetMap.set(sku, info);
    return;
  }
  const sig = matchingInfoSignature(info);
  const list = Array.isArray(cur) ? cur : [cur];
  if (list.some((x) => matchingInfoSignature(x) === sig)) return;
  targetMap.set(sku, [...list, info]);
}

/** Invoke fn(info, sku) for every stored membership (handles legacy single-object values). */
export function forEachMatchingInfo(map, fn) {
  if (!map || typeof fn !== 'function') return;
  map.forEach((val, sku) => {
    const infos = Array.isArray(val) ? val : [val];
    infos.forEach((info) => fn(info, sku));
  });
}

/**
 * True when the SKU belongs to a matching group that defines the selected variant group.
 * Membership is enough — do not require the dataset colour/flavour string to match a variant name.
 */
export function itemMatchesSelectedVariant(item, selected, matchingGroups) {
  if (!selected || selected === 'All') return true;
  const infos = matchingInfosForSku(matchingGroups, item?.title);
  return infos.some((info) =>
    (info?.variantGroups || []).some((vg) => (vg.name || '').trim() === selected)
  );
}

/**
 * Build sku -> membership map from API `/jti/matching-groups` tables payload.
 * @param {Record<string, {groups: Array}>} tables
 * @returns {Map<string, object|object[]>}
 */
export function buildMatchingGroupsMapFromTables(tables) {
  const groupsMap = new Map();
  if (!tables || typeof tables !== 'object') return groupsMap;

  Object.entries(tables).forEach(([storageKey, parsed]) => {
    if (!parsed?.groups || !Array.isArray(parsed.groups)) return;
    const { country, retailer } = parseCountryRetailerFromStorageKey(storageKey);

    parsed.groups.forEach((group) => {
      if (!group.name || !group.skus || !Array.isArray(group.skus)) return;
      const variants = getFlatVariantsFromGroup(group);
      const variantGroups = group.variantGroups?.length
        ? group.variantGroups
        : (group.variants?.length ? [{ name: '', variants: group.variants }] : []);
      group.skus.forEach((sku) => {
        if (!sku) return;
        const skuKey = typeof sku === 'string' ? sku : sku?.title || sku;
        mergeMatchingSkuInfo(groupsMap, skuKey, {
          groupName: group.name,
          modelGroup: (group.modelGroup || '').trim(),
          groupSkus: group.skus,
          variants,
          variantGroups,
          country,
          retailer,
        });
      });
    });
  });

  return groupsMap;
}

/**
 * Merge matching reference tables for multiple countries from server tables.
 * Used by Product Matching UI when several countries share the same retailer.
 */
export function mergeReferenceTablesForCountries(serverTables, countries, retailer) {
  const allGroups = new Map();
  const allModelGroups = new Map(); // name -> { id, name }

  (countries || []).forEach((country) => {
    const key = getMatchingStorageKey(country, retailer);
    const table = serverTables?.[key];
    const groups = Array.isArray(table?.groups) ? table.groups : [];
    (Array.isArray(table?.modelGroups) ? table.modelGroups : []).forEach((mg) => {
      const name = (mg?.name || '').trim();
      if (!name) return;
      if (!allModelGroups.has(name)) {
        allModelGroups.set(name, { id: mg.id || generateMatchingId(), name });
      }
    });
    groups.forEach((group) => {
      if (!group.name) return;
      const groupKey = group.name;
      if (!allGroups.has(groupKey)) {
        allGroups.set(groupKey, {
          id: group.id || generateMatchingId(),
          name: group.name,
          modelGroup: (group.modelGroup || '').trim(),
          skus: [],
          variantGroups: [],
        });
      }
      const existingGroup = allGroups.get(groupKey);
      const incomingModelGroup = (group.modelGroup || '').trim();
      if (incomingModelGroup) {
        existingGroup.modelGroup = incomingModelGroup;
      }
      if (!existingGroup.variantGroups) existingGroup.variantGroups = [];
      const incoming = (group.variantGroups?.length
        ? group.variantGroups
        : (group.variants?.length ? [{ id: generateMatchingId(), name: '', variants: group.variants }] : []));
      incoming.forEach((vg) => {
        const match = existingGroup.variantGroups.find((v) => v.name === (vg.name || ''));
        if (match) {
          const inOtherVg = (variant) => existingGroup.variantGroups.some((x) => x !== match && (x.variants || []).includes(variant));
          (vg.variants || []).forEach((v) => {
            const t = sanitizeVariant(v);
            if (t && !match.variants.includes(t) && !(match.disabledVariants || []).includes(t) && !inOtherVg(t)) {
              match.variants.push(t);
            }
          });
          (vg.disabledVariants || []).forEach((v) => {
            const t = sanitizeVariant(v);
            if (t && !(match.disabledVariants || []).includes(t)) {
              if (!match.disabledVariants) match.disabledVariants = [];
              match.disabledVariants.push(t);
            }
          });
        } else {
          const sanitized = (vg.variants || []).map((v) => sanitizeVariant(v)).filter(Boolean);
          const sanitizedDisabled = (vg.disabledVariants || []).map((v) => sanitizeVariant(v)).filter(Boolean);
          existingGroup.variantGroups.push({
            id: vg.id || generateMatchingId(),
            name: vg.name || '',
            variants: sanitized,
            disabledVariants: sanitizedDisabled,
          });
        }
      });
      group.skus.forEach((sku) => {
        const skuKey = typeof sku === 'string' ? sku : sku.title;
        if (!existingGroup.skus.some((s) => {
          const sKey = typeof s === 'string' ? s : s.title;
          return sKey === skuKey;
        })) {
          existingGroup.skus.push(sku);
        }
      });
    });
  });

  allGroups.forEach((group) => {
    const name = (group.modelGroup || '').trim();
    if (name && !allModelGroups.has(name)) {
      allModelGroups.set(name, { id: generateMatchingId(), name });
    }
  });

  return {
    groups: Array.from(allGroups.values()),
    modelGroups: Array.from(allModelGroups.values()).sort((a, b) => a.name.localeCompare(b.name)),
  };
}

function parsePriceLike(raw) {
  if (raw === null || raw === undefined) return NaN;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;
  const s = String(raw).trim();
  if (!s) return NaN;
  let cleaned = s.replace(/[^\d.,-]/g, '');
  if (!cleaned) return NaN;
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  if (hasComma && hasDot) cleaned = cleaned.replace(/,/g, '');
  else if (hasComma && !hasDot) cleaned = cleaned.replace(/,/g, '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

function calendarDateKey(product) {
  const raw = product.updated_at || product.date;
  if (!raw) return 'no-date';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(raw);
  }
}

function representativePriceForRow(p) {
  const actual = parsePriceLike(p.actual_price ?? p.price ?? p.current_price);
  if (Number.isFinite(actual) && actual > 0) return actual;
  const reg = parsePriceLike(p.regular_price ?? p.original_price);
  if (Number.isFinite(reg) && reg > 0) return reg;
  return NaN;
}

/**
 * Check if prices differ for products in a group across calendar days.
 * Products are grouped by (country, retailer, title) and we compare prices per day.
 * @param {Array} groupProducts - Products in the same matching group (can have multiple rows per product for different dates)
 * @returns {boolean} - true if prices differ on any day across products
 */
export function pricesDifferInGroup(groupProducts) {
  if (!groupProducts || groupProducts.length <= 1) return false;

  const productsByKey = new Map();
  groupProducts.forEach((product) => {
    const key = `${product.country || ''}-${product.retailer || ''}-${product.title || ''}`;
    if (!productsByKey.has(key)) {
      productsByKey.set(key, []);
    }
    productsByKey.get(key).push(product);
  });

  if (productsByKey.size <= 1) return false;

  const byDate = new Map();
  groupProducts.forEach((product) => {
    const dateKey = calendarDateKey(product);
    if (!byDate.has(dateKey)) {
      byDate.set(dateKey, []);
    }
    byDate.get(dateKey).push(product);
  });

  const EPS = 0.015;

  for (const [, productsAtDate] of byDate.entries()) {
    const productsAtDateByTitle = new Map();
    productsAtDate.forEach((p) => {
      const title = p.title || '';
      if (!productsAtDateByTitle.has(title)) {
        productsAtDateByTitle.set(title, []);
      }
      productsAtDateByTitle.get(title).push(p);
    });

    const prices = Array.from(productsAtDateByTitle.values())
      .map((productRows) => representativePriceForRow(productRows[0]))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (prices.length < 2) continue;

    const firstPrice = prices[0];
    if (prices.some((p) => Math.abs(p - firstPrice) > EPS)) {
      return true;
    }
  }

  return false;
}

/**
 * Group products by matching groups and return processed list.
 * Products are only grouped if they belong to the same (country, retailer) combination.
 * @param {Array} products - Products to group
 * @param {Map} matchingGroups - Map from buildMatchingGroupsMapFromTables() (sku -> membership or membership[])
 * @returns {Array} - Processed products (grouped where prices match, separate where they differ)
 */
export function groupProductsByMatching(products, matchingGroups) {
  if (!products || products.length === 0 || !matchingGroups || matchingGroups.size === 0) {
    return products;
  }

  const groupMap = new Map();
  const ungroupedProducts = [];

  products.forEach((product) => {
    const infos = matchingInfosForSku(matchingGroups, product.title);
    const productCountry = (product.country || '').toLowerCase();
    const productRetailer = (product.retailer || '').toLowerCase();
    const matchingInfos = infos.filter((info) => {
      const groupCountry = (info.country || '').toLowerCase();
      const groupRetailer = (info.retailer || '').toLowerCase();
      return productCountry === groupCountry && productRetailer === groupRetailer;
    });

    if (matchingInfos.length === 0) {
      ungroupedProducts.push(product);
      return;
    }

    matchingInfos.forEach((groupInfo) => {
      const groupCountry = (groupInfo.country || '').toLowerCase();
      const groupRetailer = (groupInfo.retailer || '').toLowerCase();
      const groupKey = `${groupCountry}-${groupRetailer}-${groupInfo.groupName}`;
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          groupName: groupInfo.groupName,
          groupSkus: groupInfo.groupSkus,
          country: groupInfo.country,
          retailer: groupInfo.retailer,
          products: [],
        });
      }
      groupMap.get(groupKey).products.push(product);
    });
  });

  const result = [...ungroupedProducts];

  groupMap.forEach((groupData) => {
    const groupProducts = groupData.products;

    if (pricesDifferInGroup(groupProducts)) {
      result.push(...groupProducts);
    } else {
      const byDate = new Map();
      groupProducts.forEach((product) => {
        const dateKey = calendarDateKey(product);
        if (!byDate.has(dateKey)) {
          byDate.set(dateKey, []);
        }
        byDate.get(dateKey).push(product);
      });

      byDate.forEach((productsAtDate) => {
        const sorted = [...productsAtDate].sort((a, b) => {
          const aHasImage = a.image && a.image !== 'null' && a.image.trim() !== '';
          const bHasImage = b.image && b.image !== 'null' && b.image.trim() !== '';
          if (aHasImage && !bHasImage) return -1;
          if (!aHasImage && bHasImage) return 1;
          return (a.title || '').localeCompare(b.title || '');
        });
        const representative = sorted[0];
        result.push({
          ...representative,
          _originalTitle: representative.title,
          title: groupData.groupName,
          _isGroupRepresentative: true,
          _groupSkus: groupData.groupSkus,
        });
      });
    }
  });

  return result;
}

function normalizeStickGroupName(name) {
  return sanitizeVariant(name).toLowerCase();
}

function categoryFromGroupName(groupName) {
  const normalized = normalizeStickGroupName(groupName);
  if (!normalized) return null;
  if (STICK_CATEGORY_HNS_GROUP_NAMES.has(normalized)) return 'HNS';
  if (STICK_CATEGORY_HTS_GROUP_NAMES.has(normalized)) return 'HTS';
  return null;
}

function categoryFromTitle(title) {
  const lower = (title || '').toLowerCase();
  if (!lower) return 'Other';
  const lineMatchesCategory = (lines, category) =>
    lines.some((line) => {
      const escaped = line.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`\\b${escaped}\\b`, 'i').test(lower);
    });
  if (lineMatchesCategory(STICK_CATEGORY_HNS_LINES, 'HNS')) return 'HNS';
  if (lineMatchesCategory(STICK_CATEGORY_HTS_LINES, 'HTS')) return 'HTS';
  return 'Other';
}

function categoryFromMatchingVariantGroups(infos, item) {
  if (!item) return null;
  const itemVariants = [
    ...extractVariantsFromRaw(item.variant),
    ...extractVariantsFromRaw(item.product_variant_names),
    ...extractVariantsFromRaw(item.product_variant),
  ]
    .map((v) => sanitizeVariant(v).toUpperCase())
    .filter(Boolean);

  for (const info of infos) {
    for (const vg of info?.variantGroups || []) {
      const vgName = (vg.name || '').trim().toLowerCase();
      if (!STICK_CATEGORY_VARIANT_GROUP_NAMES.includes(vgName)) continue;
      const groupVariants = (vg.variants || [])
        .map((v) => sanitizeVariant(v).toUpperCase())
        .filter(Boolean);
      for (const iv of itemVariants) {
        if (iv === 'HTS' || iv === 'HNS') return iv;
        if (groupVariants.includes(iv)) {
          if (iv === 'OTHER') return 'Other';
          if (iv === 'HTS' || iv === 'HNS') return iv;
        }
      }
    }
  }
  return null;
}

/**
 * Resolve stick category: HTS (heated tobacco sticks), HNS (heated non-tobacco), or Other.
 * Uses matching-group variant assignment, then matching group name, then title keywords.
 */
export function resolveStickCategory(title, matchingGroups, item = null) {
  const infos = matchingInfosForSku(matchingGroups, title);

  const fromVariantGroups = categoryFromMatchingVariantGroups(infos, item);
  if (fromVariantGroups) return fromVariantGroups;

  for (const info of infos) {
    const fromGroup = categoryFromGroupName(info?.groupName || '');
    if (fromGroup) return fromGroup;
  }

  return categoryFromTitle(title);
}

/** Returns true when item matches the selected stick category filter value (All / HTS / HNS / Other). */
export function matchesStickCategoryFilter(item, matchingGroups, selectedCategory) {
  if (!selectedCategory || selectedCategory === 'All') return true;
  const category = resolveStickCategory(item?.title, matchingGroups, item);
  if (selectedCategory === 'Other') return category === 'Other';
  return category === selectedCategory;
}
